import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  cancelGeneration,
  retryGeneration,
  retryGenerationRow,
} from '@platform/client';
import {
  isRetryable,
  isTerminal,
  retryableRows,
} from '@platform/schema/generation';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { counted } from '../library/counted';
import { jobQuery, jobsQuery } from '../library/sources';
import { PageHeader, SCREEN_GAP } from '../routes/PageHeader';

import { BulkProgress, ROW_PLAIN, statusLabel } from './BulkProgress';
import { explainFailure } from './explain';
import { JobNow } from './JobNow';
import { JobPrompts } from './JobPrompts';
import { publishReadinessAll, publishReadinessQuery } from './readiness';
import { elapsedOf, STAGE, stageOf } from './stage';
import { useNow } from './useNow';
import { JobVerification } from './Verification';

import type { ChipProps } from '@mui/material/Chip';
import type { SubtreeReadiness } from '@platform/schema/authoring';
import type {
  GenerationJob,
  GenerationList,
  GenerationRow,
  JobKind,
  JobStatus,
  RowKind,
  RowStatus,
  ToolCall,
} from '@platform/schema/generation';

/**
 * The job list and one job's rows — spec 013 §11.
 *
 * **Polled, not streamed.** §5 refused a `GenerationEvent` table because fine-grained
 * narration is not worth infrastructure, and the same argument finishes here: row status
 * is the durable state, one admin is watching, and a two-second poll says everything a
 * stream would without a lifecycle to get wrong.
 */

/**
 * What the job did, in a word — a whole CSV, or one piece of content written again.
 *
 * A rewrite queues one row named after the module the scene lives in, so on this table it
 * read as an import of that module: same slug, same green, and nothing to open, because a
 * rewrite lands in a module that already exists rather than producing one.
 */
const KIND_LABEL: Record<JobKind, string> = {
  INGEST: 'import',
  REGENERATE: 'rewrite',
  VERIFY: 'verify',
  REVIEW: 'review',
};

/**
 * Exhaustive on purpose: adding a `JobStatus` should not compile until somebody decides
 * how the console shows it. `PARTIAL` arrived that way.
 */
const JOB_COLOUR: Record<
  JobStatus,
  'default' | 'info' | 'success' | 'warning' | 'error'
> = {
  QUEUED: 'default',
  RUNNING: 'info',
  REVIEWING: 'info',
  SUCCEEDED: 'success',
  // Not `success` and not `error`. Some rows produced drafts worth reviewing and some
  // produced nothing, and a run that half worked reads as neither.
  PARTIAL: 'warning',
  FAILED: 'error',
  CANCELLED: 'default',
};

/**
 * The status, and how much of the run it covers.
 *
 * A bare `PARTIAL` says a run half worked without saying which half, and the first thing
 * anyone asks is how many. The counts come from the job, so this needs no second request.
 *
 * Counted in lessons rather than rows since GEN-92: a fifty-lesson import is sixty-odd
 * rows once its plans and reviews are counted, and "43/62" is a number nobody asked for.
 * A job with no lesson rows — a rewrite — falls back to its rows, which is what it has.
 */
function rowsLabel(
  job: Pick<
    GenerationJob,
    'status' | 'lessons' | 'rowsSucceeded' | 'rowsTotal'
  >,
): string {
  const label = statusLabel(job);

  const [done, total] =
    job.lessons.total > 0
      ? [job.lessons.succeeded, job.lessons.total]
      : [job.rowsSucceeded, job.rowsTotal];

  if (total === 0) return label;

  // Its lessons are written, but the job is not done until the review is.
  const word = job.status === 'REVIEWING' ? 'written' : 'done';

  return `${label} · ${String(done)} of ${String(total)} ${word}`;
}

type ListedJob = GenerationList['jobs'][number];

/**
 * What a list row is called — GEN-111. The course alone made two imports of one course
 * the same line, so the modules name it, two and then a count. A job from before the
 * modules were recorded has only its course.
 */
function jobTitle(job: Pick<ListedJob, 'label' | 'modules'>): string {
  const [first, second, ...rest] = job.modules;

  if (first === undefined) return job.label;
  if (second === undefined) return first;

  const shown = `${first}, ${second}`;
  return rest.length === 0 ? shown : `${shown} +${String(rest.length)} more`;
}

/** The smaller line under the title: the file it came from, or that it is a rewrite. */
const jobSource = (job: Pick<ListedJob, 'kind' | 'fileName'>) =>
  job.kind === 'REGENERATE' ? 'Rewrite' : job.fileName;

/**
 * Lessons once the planner has counted them; before that, what the file holds — GEN-111.
 * A queued row read "—", which was half of why two of them looked the same.
 */
function lessonsCell(
  job: Pick<ListedJob, 'kind' | 'lessons' | 'modules'>,
): string {
  if (job.lessons.total > 0) return String(job.lessons.total);
  if (job.kind !== 'INGEST' || job.modules.length === 0) return '—';

  return job.modules.length === 1
    ? '1 module'
    : `${String(job.modules.length)} modules`;
}

/** What a row is a unit of, for a column that is otherwise five identical slugs. */
const ROW_KIND_LABEL: Record<RowKind, string> = {
  MODULE: 'module',
  RESEARCH: 'research',
  PLAN: 'outline',
  LESSON: 'lesson',
  REVIEW: 'review',
  SCENE: 'scene',
  VERIFY: 'verify',
};

/**
 * Where the module this row produced actually stands — GEN-23, GEN-24.
 *
 * Asked of the readiness endpoint rather than stored on the row. A blocker count written
 * at generation time is stale the moment a reviewer edits a scene, and `SUCCEEDED · 5/5`
 * said nothing at all about whether any of the five could be published — the first
 * partner run finished green with nine unmet rules waiting on the publish screen.
 */
function ModuleState({ collectionId }: { collectionId: string }) {
  const state = useQuery(publishReadinessQuery(collectionId)).data?.data;
  if (state === undefined) return null;

  const { unmet, ...chip } = moduleChip(state);
  if (unmet === undefined) return <Chip size="small" {...chip} />;

  // `describeChild` keeps "n unmet" as the chip's name and makes the list its description;
  // the tab stop lets a keyboard open it, which a plain chip cannot.
  return (
    <Tooltip
      describeChild
      title={
        <Stack component="ul" sx={{ m: 0, pl: 2 }}>
          {unmet.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </Stack>
      }
    >
      <Chip size="small" tabIndex={0} {...chip} />
    </Tooltip>
  );
}

/**
 * Ready only once nothing is left to object — GEN-121. A review still to run has
 * filed no blockers yet, and the module page refuses on the ones it then files.
 */
function moduleChip(state: SubtreeReadiness): {
  color: ChipProps['color'];
  label: string;
  unmet?: readonly string[];
} {
  if (state.status === 'PUBLISHED')
    return { color: 'success', label: 'published' };
  if (state.issues.length > 0)
    return {
      color: 'warning',
      label: `${String(state.issues.length)} unmet`,
      unmet: state.issues.map(
        (issue) => `${issue.item.title}: ${issue.message}`,
      ),
    };
  if (state.review === 'RUNNING')
    return { color: 'default', label: 'in review' };
  if (state.review === 'NOT_RUN')
    return { color: 'warning', label: 'not reviewed' };
  if (state.openBlockers > 0)
    return {
      color: 'warning',
      label: counted(state.openBlockers, 'blocker'),
    };
  return { color: 'info', label: 'ready to publish' };
}

/**
 * How far into its module a row has got — GEN-38. Printed, not computed: the pair arrives
 * on the row, and a row that has not planned yet has no honest number to give.
 */
const lessonsDone = (
  row: Pick<GenerationRow, 'planned' | 'completed'>,
): string =>
  row.planned === undefined
    ? '—'
    : `${String(row.completed)} / ${String(row.planned)}`;

/**
 * What a row leaves behind: a way into what it produced, or what went wrong in terms a
 * reviewer can act on. The raw error stays available as the tooltip, because that is the
 * text a bug report needs and the explanation is not a replacement for it.
 */
function RowDetail({
  row,
}: {
  row: Pick<
    GenerationRow,
    | 'error'
    | 'collectionId'
    | 'status'
    | 'planned'
    | 'writing'
    | 'kind'
    | 'lessonTitle'
    | 'stage'
  >;
}) {
  // Only a settled row's error is its outcome; an unsettled one is being retried.
  const explained =
    row.status === 'FAILED' || row.status === 'PARTIAL'
      ? explainFailure(row.error)
      : undefined;

  if (explained !== undefined) {
    return (
      <Tooltip title={row.error ?? ''}>
        <span>
          <Typography variant="body2">{explained.title}</Typography>
          {/* A settled row keeps the stage it stopped in, and Retry resumes there. */}
          {row.stage !== undefined && (
            <Typography variant="caption" color="text.secondary" component="p">
              Stopped during: {STAGE[row.stage]}.
            </Typography>
          )}
          {explained.action !== undefined && (
            <Typography variant="caption" color="text.secondary">
              {explained.action}
            </Typography>
          )}
        </span>
      </Tooltip>
    );
  }

  // The count says how far; this says on what — GEN-38, widened by GEN-68 to the two
  // turns that report no count at all. A row that had planned nothing yet and one that
  // was being reviewed both drew an empty cell, and those are most of a row's minutes.
  const stage = stageOf(row);

  if (stage !== undefined) {
    return (
      <Typography variant="body2" color="text.secondary">
        {stage}
      </Typography>
    );
  }

  if (row.collectionId === undefined) return null;

  // The way into what the row produced. A succeeded row's module is the only thing on
  // this screen worth opening, and it was printed as a bare id nobody could act on.
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <Link to={`/library/modules/${row.collectionId}`}>module</Link>
      <ModuleState collectionId={row.collectionId} />
    </Stack>
  );
}

/** Each search, fetch and save a research row made — GEN-216. */
function RowCalls({ calls }: { calls: readonly ToolCall[] }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        size="small"
        onClick={() => {
          setOpen(!open);
        }}
      >
        {open ? 'Hide' : 'Show'} {counted(calls.length, 'tool call')}
      </Button>
      <Collapse in={open}>
        <ol>
          {calls.map((call, index) => (
            <Typography
              // Calls carry no id, and a row's list only ever grows at the end.

              key={index}
              component="li"
              variant="caption"
              color={call.failed === undefined ? 'text.secondary' : 'error'}
            >
              {call.tool.replace(/^mcp__/, '').replace('__', ' ')}: {call.input}
              {call.failed === undefined ? '' : ` (failed: ${call.failed})`}
            </Typography>
          ))}
        </ol>
      </Collapse>
    </>
  );
}

const ROW_COLOUR: Record<
  RowStatus,
  'default' | 'info' | 'success' | 'warning' | 'error'
> = {
  PENDING: 'default',
  RUNNING: 'info',
  SUCCEEDED: 'success',
  // Warning, not error — GEN-36. A partial row wrote lessons that were kept, so it needs
  // a look rather than a re-run, and colouring it red would send an operator to redo work
  // that is already there.
  PARTIAL: 'warning',
  FAILED: 'error',
  SKIPPED: 'default',
};

/**
 * `isTerminal` from the schema, which is the same four statuses plus `PARTIAL` — GEN-92.
 *
 * `PARTIAL` was missing, so an import that half worked kept its 2-second poll and its
 * progress bar forever: the runner had stopped and the screen said otherwise.
 */
const settled = isTerminal;

/**
 * Whether the job can still change — GEN-71.
 *
 * The poll, the elapsed clock and the progress bar all asked this through
 * `?? 'QUEUED'`, and a request that failed leaves no data, so each of them read a failure
 * as a job that had not started yet: a refetch every two seconds, a re-render every
 * second, and a progress bar, none of which ever stopped. A request that failed is not a
 * job that is still working.
 */
const working = (failed: boolean, status: JobStatus | undefined): boolean =>
  !failed && !settled(status ?? 'QUEUED');

function PausedAlert({ until }: { until: string }) {
  return (
    <Alert severity="warning">
      The queue is waiting on the Claude subscription until{' '}
      {new Date(until).toLocaleTimeString()}. Rows are queued, not lost —
      nothing needs re-uploading.
    </Alert>
  );
}

export function GenerationJobs() {
  // The poll lives here rather than in `jobsQuery`: this is the screen that wants the
  // queue's live state, and the modules filter shares the cache without the cost.
  const jobs = useQuery({
    ...jobsQuery(),
    staleTime: 0,
    refetchInterval: 5_000,
  });

  const now = useNow(
    jobs.data?.data.jobs.some((job) => !settled(job.status)) ?? false,
  );

  return (
    <Stack spacing={SCREEN_GAP}>
      <PageHeader
        title="Generation jobs"
        action={
          <Stack direction="row" spacing={1}>
            <Button component={Link} to="/authoring/ledger">
              Ledger
            </Button>
            <Button variant="contained" component={Link} to="/authoring/new">
              New
            </Button>
          </Stack>
        }
      />

      {jobs.isPending && <LinearProgress />}

      {jobs.data?.data.pausedUntil !== undefined && (
        <PausedAlert until={jobs.data.data.pausedUntil} />
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Import</TableCell>
            <TableCell>Kind</TableCell>
            <TableCell>Lessons</TableCell>
            <TableCell>Planner</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Queued by</TableCell>
            <TableCell>Queued</TableCell>
            <TableCell>Started</TableCell>
            <TableCell>Elapsed</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {jobs.data?.data.jobs.map((job) => (
            <TableRow key={job.id} hover>
              <TableCell>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: 'center' }}
                >
                  <Link to={`/authoring/jobs/${job.id}`}>{jobTitle(job)}</Link>
                  {job.mine && <Chip size="small" label="yours" />}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {jobSource(job)}
                </Typography>
              </TableCell>
              <TableCell>{KIND_LABEL[job.kind]}</TableCell>
              <TableCell>{lessonsCell(job)}</TableCell>
              <TableCell>{job.planner}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={JOB_COLOUR[job.status]}
                  label={rowsLabel(job)}
                />
              </TableCell>
              <TableCell>{job.queuedBy}</TableCell>
              <TableCell>{new Date(job.createdAt).toLocaleString()}</TableCell>
              <TableCell>
                {job.startedAt === undefined
                  ? '—'
                  : new Date(job.startedAt).toLocaleString()}
              </TableCell>
              <TableCell>{elapsedOf(job, now) ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
}

/**
 * Retry and cancel, sharing one refetch — GEN-92.
 *
 * Both change the same job, so both invalidate the same two keys: the job on this screen,
 * and the list behind it whose counts and status have just moved. Written once rather than
 * per button, because a mutation that forgot one of them leaves a screen that says the
 * import is still failed after it has been re-queued.
 */
export function useJobActions(id: string) {
  const client = useQueryClient();

  // Curriculum's rows too: a build job's lessons are its rows' status — CURR-29.
  const settle = async (): Promise<void> => {
    await Promise.all([
      client.invalidateQueries({ queryKey: jobQuery(id).queryKey }),
      client.invalidateQueries({ queryKey: ['generations'] }),
      client.invalidateQueries({ queryKey: ['curriculum', 'lessons'] }),
    ]);
  };

  const retryAll = useTrackedMutation('Retry all failed', {
    mutationFn: () => retryGeneration({ path: { id }, throwOnError: true }),
    onSuccess: settle,
  });

  const cancel = useTrackedMutation('Cancel import', {
    mutationFn: () => cancelGeneration({ path: { id }, throwOnError: true }),
    onSuccess: settle,
  });

  const retryRow = useTrackedMutation<unknown, Error, string>('Retry lesson', {
    mutationFn: (rowId: string) =>
      retryGenerationRow({ path: { id: rowId }, throwOnError: true }),
    onSuccess: settle,
  });

  return { retryAll, cancel, retryRow };
}

export function GenerationJob() {
  const { id = '' } = useParams<{ id: string }>();

  const navigate = useNavigate();
  const job = useQuery({
    ...jobQuery(id),
    // Stops polling once the job cannot change again, rather than asking forever.
    refetchInterval: (query) =>
      working(query.state.status === 'error', query.state.data?.status)
        ? 2_000
        : false,
  });

  const detail = job.data;
  const live = working(job.isError, detail?.status);

  // A review settling moves its module's readiness, which is otherwise cached for minutes.
  // Keyed on every review row, not the job: one module's review can finish while the job
  // still waits on another's.
  const reviews = detail?.rows
    .filter((row) => row.kind === 'REVIEW')
    .map((row) => row.status)
    .join();
  const client = useQueryClient();
  useEffect(() => {
    void client.invalidateQueries({ queryKey: publishReadinessAll });
  }, [client, reviews]);
  const actions = useJobActions(id);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const now = useNow(live);

  return (
    <Stack spacing={SCREEN_GAP}>
      {/* The status is the action slot rather than part of the heading text. Nested in
          the heading's `Typography` it joined the heading's accessible name, so the row
          count was read out as part of the job's identity and changed while you sat on
          the screen. */}
      <PageHeader
        title={`Job ${id.slice(-8)}`}
        subtitle={
          detail === undefined
            ? undefined
            : `${KIND_LABEL[detail.kind]} · planner ${detail.planner}`
        }
        action={
          detail === undefined ? undefined : (
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center', flexWrap: 'wrap' }}
            >
              <Chip
                size="small"
                color={JOB_COLOUR[detail.status]}
                label={rowsLabel(detail)}
              />
              {/* Offered only where it would do something. A "retry all failed" on a
                  clean import is a button whose only outcome is confusion. */}
              {retryableRows(detail.lessons) > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={actions.retryAll.isPending}
                  onClick={() => {
                    actions.retryAll.mutate();
                  }}
                >
                  Retry all failed
                </Button>
              )}
              {live && (
                <Button
                  size="small"
                  color="warning"
                  disabled={actions.cancel.isPending}
                  onClick={() => {
                    setConfirmingCancel(true);
                  }}
                >
                  Cancel
                </Button>
              )}
            </Stack>
          )
        }
      />

      <Dialog
        open={confirmingCancel}
        onClose={() => {
          setConfirmingCancel(false);
        }}
      >
        <DialogTitle>Cancel this job?</DialogTitle>
        <DialogContent>
          <Typography>Lessons it has already written are kept.</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmingCancel(false);
            }}
          >
            Keep running
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={actions.cancel.isPending}
            onClick={() => {
              actions.cancel.mutate(undefined, {
                onSuccess: () => {
                  setConfirmingCancel(false);
                },
              });
            }}
          >
            Cancel job
          </Button>
        </DialogActions>
      </Dialog>

      {detail?.pausedUntil !== undefined && (
        <PausedAlert until={detail.pausedUntil} />
      )}

      {detail?.waitingOn !== undefined && (
        <Alert severity="info">
          Waiting for{' '}
          <Link to={`/authoring/jobs/${detail.waitingOn.id}`}>
            {detail.waitingOn.label}
          </Link>{' '}
          to finish. Imports run one at a time, so this one starts as soon as
          that one is done — nothing needs doing.
        </Alert>
      )}

      {detail !== undefined && <BulkProgress job={detail} now={now} />}

      {detail !== undefined && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <JobNow job={detail} now={now} />
        </Paper>
      )}

      {detail !== undefined && <JobPrompts job={detail} />}

      {detail?.rows.some((row) => row.kind === 'VERIFY') === true && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <JobVerification id={id} />
        </Paper>
      )}

      {job.isError && (
        <Alert
          severity="error"
          action={
            <Stack direction="row" spacing={1}>
              <Button
                color="inherit"
                size="small"
                onClick={() => void job.refetch()}
              >
                Retry
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  void navigate(-1);
                }}
              >
                Back
              </Button>
            </Stack>
          }
        >
          This job could not be loaded.
        </Alert>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            {/* Numbered by where the row is on this screen, not by `position` — GEN-92
                hands positions out in strides of 22 so a plan row's lessons have somewhere
                reserved to go, and `#23` for the second module is a number about the
                queue rather than about the import. */}
            <TableCell>#</TableCell>
            <TableCell>Module</TableCell>
            <TableCell>Unit</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Lessons</TableCell>
            <TableCell>Elapsed</TableCell>
            <TableCell>Detail</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {(detail?.rows ?? []).map((row, index) => (
            <TableRow key={row.id}>
              <TableCell>{index + 1}</TableCell>
              <TableCell>{row.lessonTitle ?? row.moduleSlug}</TableCell>
              <TableCell>{ROW_KIND_LABEL[row.kind]}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={ROW_COLOUR[row.status]}
                  label={ROW_PLAIN[row.status]}
                />
              </TableCell>
              <TableCell>{lessonsDone(row)}</TableCell>
              <TableCell>{elapsedOf(row, now) ?? '—'}</TableCell>
              <TableCell>
                <RowDetail row={row} />
                {row.calls !== undefined && <RowCalls calls={row.calls} />}
              </TableCell>
              <TableCell>
                {isRetryable(row.status) && (
                  <Button
                    size="small"
                    disabled={actions.retryRow.isPending}
                    onClick={() => {
                      actions.retryRow.mutate(row.id);
                    }}
                  >
                    Retry
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
}
